
import React from 'react';
import { useDebugInterviewRequest } from '@/hooks/interview/useDebugInterviewRequest';
import { useFormState } from '@/hooks/interview/useFormState';
import { useFormSubmission } from '@/hooks/interview/useFormSubmission';
import InfoBox from './InfoBox';
import PersonalInfoFields from './PersonalInfoFields';
import QuestionDisplay from './QuestionDisplay';
import AnswerSection from './AnswerSection';
import SubmitButton from './SubmitButton';
import ErrorDisplay from './ErrorDisplay';
import LoadingIndicator from './LoadingIndicator';

interface DebugInterviewSubmissionFormContainerProps {
  requestLink: string;
  isPageLoading?: boolean;
}

const DebugInterviewSubmissionFormContainer: React.FC<DebugInterviewSubmissionFormContainerProps> = ({ 
  requestLink, 
  isPageLoading = false 
}) => {
  const {
    loading,
    error,
    interviewRequest,
    debugInfo,
    networkInfo
  } = useDebugInterviewRequest({ requestLink, isPageLoading });

  const {
    answer,
    setAnswer,
    name,
    setName,
    title,
    setTitle,
    company,
    setCompany,
    photoUrl,
    setPhotoUrl,
    wordCount
  } = useFormState();

  const { submitting, handleSubmit: submitForm } = useFormSubmission({ requestLink });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    await submitForm({
      answer,
      name,
      title,
      company,
      photoUrl
    });
  };

  // Debug container rendering

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error && !interviewRequest) {
    return <ErrorDisplay error={error} />;
  }

  if (!interviewRequest && !loading) {
    return <ErrorDisplay error="No interview request available" />;
  }

  return (
    <div className="bg-white rounded-lg shadow p-8 max-w-4xl mx-auto">
      
      {interviewRequest && (
        <form onSubmit={handleSubmit} id="interview-form" className="space-y-6">
          <InfoBox />
          
          <div className="space-y-4">
            <PersonalInfoFields 
              name={name}
              setName={setName}
              title={title}
              setTitle={setTitle}
              company={company}
              setCompany={setCompany}
              photoUrl={photoUrl}
              setImageUrl={setPhotoUrl}
            />
            
            <QuestionDisplay 
              question={interviewRequest.question}
              context={interviewRequest.context}
              contextUrl={interviewRequest.context_url}
            />
            
            <AnswerSection 
              answer={answer}
              setAnswer={setAnswer}
              wordCount={wordCount}
            />
          </div>

          <SubmitButton submitting={submitting} />
        </form>
      )}
    </div>
  );
};

export default DebugInterviewSubmissionFormContainer;
